import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import Loader from "@/shared/ui/Loader";
import DeviceChangeButton from "@/shared/widgets/DeviceChangeButton";
import DeviceContentWrapper from "@/shared/widgets/DeviceContentWrapper";
import DeviceTabButton from "@/shared/widgets/DeviceTabButton";
import DeviceTabsWrapper from "@/shared/widgets/DeviceTabsWrapper";
import { DeviceEnum } from "@/domains/business/constants/deviceEnum";
import {
  appCurrentTabSelector,
  clearSlice,
  infoSelector,
  setCurrentTab,
} from "@/domains/business/slice";
import { tabltetIsShow } from "@/domains/tablet/slice";
import { getBusinessTabContent } from "@/domains/business/helpers/getBusinessTabContent";
import closeTablet from "@/domains/tablet/helpers/closeTablet";
import useLoadPlayerBusinesses from "@/domains/business/hooks/useLoadPlayerBusinesses";
import BusinessSelectContainer from "@/domains/business/containers/tablet/tabs/businessSelect/BusinessSelectContainer";

export default function BusinessTabletContainer() {
  const { t } = useTranslation("business");
  const dispatch = useAppDispatch();

  const appCurrentTab = useAppSelector(appCurrentTabSelector);
  const info = useAppSelector(infoSelector);
  const isShowTablet = useAppSelector(tabltetIsShow);

  const [isFirstRender, setIsFirstRender] = useState(true);

  const { isPending, loadBusinessesInfo } = useLoadPlayerBusinesses();

  const tabContent = useMemo(
    () => getBusinessTabContent(DeviceEnum.TABLET, info?.role),
    [info?.role],
  );
  const Component = tabContent ? tabContent.tabsMap[appCurrentTab] : null;
  const clearInfo = useCallback(() => {
    dispatch(clearSlice());
    loadBusinessesInfo();
  }, [dispatch, loadBusinessesInfo]);

  useLayoutEffect(() => {
    loadBusinessesInfo();
  }, [loadBusinessesInfo]);

  useEffect(() => {
    if (!isShowTablet) dispatch(clearSlice("saveInfo"));
  }, [dispatch, isShowTablet]);

  useEffect(() => setIsFirstRender(false), []);

  let content = <Loader />;

  if (appCurrentTab) {
    content = (
      <div className="size-full bg-gray-950" data-mz-accent="lightBlue">
        <DeviceContentWrapper>
          {!info && <BusinessSelectContainer isLoading={isPending} />}
          <DeviceTabsWrapper
            logoSrc="/svg/business/my-business.svg"
            title={t("business.header.title")}
            description={t("business.header.description")}
            isBackground
            className="bg-gradient-to-r from-[#007FDB] to-[#0164AC]"
          >
            {tabContent?.tabsList.map((item) => (
              <DeviceTabButton
                key={item}
                tab={item}
                active={appCurrentTab === item}
                text={t(`business.tabs.${item}`)}
                iconCategory="business/tabs"
                iconName={item}
                onClick={() => dispatch(setCurrentTab(item))}
              />
            ))}
            <DeviceChangeButton
              title={t("business.changeBusiness")}
              description={t("business.toggleBusiness")}
              onClick={clearInfo}
              background="#00518B"
              isBackground
            />
          </DeviceTabsWrapper>
          {Component && (
            <Component
              onClose={closeTablet}
              isFirstRender={isFirstRender}
              info={info}
              device={DeviceEnum.TABLET}
            />
          )}
        </DeviceContentWrapper>
      </div>
    );
  }

  return content;
}
